<template>
    <v-bottom-sheet v-model="dialog" v-stack="{ open: dialog, close: hide }" :max-width="560" @input="onSheetInput"
        content-class="pfadd-dialog">
        <div class="pfadd-sheet">
            <div class="pfadd-header">
                <v-btn icon small class="pfadd-back" @click="hide">
                    <v-icon size="18">mdi-chevron-left</v-icon>
                </v-btn>
                <span class="pfadd-title">{{ $t('common.add_block_to_routine') }}</span>
            </div>

            <div class="pfadd-body">
                <div class="ab-filters">
                    <kimsa-combobox :placeholder="$t('common.folder')" dense :items="folderOptions" item-text="label"
                        @change="folder = $event" @clear="folder = null" clearable ref="folders" />
                    <kimsa-text-field :placeholder="$t('common.search')" :value="search" @change="search = $event"
                        prepend-inner-icon="mdi-magnify" dense clearable no-elevation />
                </div>

                <div v-if="loadingSearch" class="ab-msg">
                    <v-progress-circular indeterminate size="22" :color="accentColor" />
                </div>
                <div v-else-if="blocks.length === 0" class="ab-msg">{{ $t('common.no_blocks_founded') }}</div>

                <div v-else class="ab-list">
                    <div v-for="b in blocks" :key="b.objectID" class="ab-row" @click="onSelect(b)">
                        <div class="ab-icon"><v-icon size="20" :color="accentColor">mdi-view-dashboard-outline</v-icon>
                        </div>
                        <div class="ab-info">
                            <div class="ab-name text-truncate">{{ b.name }}</div>
                            <div class="ab-meta">{{ b.block.exercises.length }}
                                {{ $tc('common.exercises', b.block.exercises.length).toLowerCase() }}</div>
                        </div>
                        <v-btn icon small class="ab-add">
                            <v-icon size="18">mdi-plus</v-icon>
                        </v-btn>
                    </div>
                </div>

                <kimsa-pagination v-if="pageCount > 1" class="mt-3" center @page="onPageChange" :value="page + 1"
                    :pageCount="pageCount" :total-visible="isMobile ? 4 : 7" />
            </div>
        </div>
    </v-bottom-sheet>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { BlockIndex } from "@/utils/Algolia";
import { debounce } from "@/utils/Common";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaPagination from "@/components/common/KimsaPagination";

export default {
    name: "AddExistingBlockDialog",
    components: { KimsaCombobox, KimsaTextField, KimsaPagination },
    props: {
        coachId: {
            type: String,
            default: null,
        },
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            dialog: false,
            folder: null,
            search: '',
            blocks: [],
            loadingSearch: false,
            page: 0,
            pageCount: 0,
        }
    },
    computed: {
        ...mapState(['folders', 'globalFolders']),
        accentColor(vm = this) {
            return vm.$vuetify.theme.dark ? 'primary' : 'accent'
        },
        folderOptions(vm = this) {
            return [...vm.globalFolders, ...vm.folders].filter(f => f.type === 'blocks').map(f => ({ ...f, id: f.id, label: vm.folderLabel(f) })) || []
        },
    },
    async mounted() {
        const vm = this
        if (vm.folders.length === 0) await vm.bindFolders(vm.coachId)
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
    },
    methods: {
        ...mapActions(['bindFolders', 'bindGlobalFolders']),
        show() {
            const vm = this
            vm.dialog = true
            vm.loadBlocks()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onSheetInput(val) {
            if (!val && this.dialog) this.hide()
        },
        reset() {
            const vm = this
            vm.folder = null
            vm.search = ''
            vm.blocks = []
            vm.page = 0
            vm.pageCount = 0
            if (vm.$refs.folders) vm.$refs.folders.model = null
        },
        debouncedLoadBlocks: debounce(function () {
            this.loadBlocks()
        }, 500),
        async loadBlocks() {
            const vm = this
            if (!vm.coachId) return
            await vm.withLoading(async () => {
                let filter = `coachId: ${vm.coachId}`
                if (vm.folder) filter += ` AND folder: ${vm.folder.id}`
                const response = await BlockIndex.search(vm.search, { filters: filter, hitsPerPage: 8, page: vm.page })
                vm.blocks = response.hits
                vm.pageCount = response.nbPages || 0
            }, 'loadingSearch')
        },
        onPageChange(p) {
            const vm = this
            vm.page = Number(p) - 1
            vm.loadBlocks()
        },
        onSelect(block) {
            const vm = this
            vm.$emit('select', block)
            vm.hide()
        },
    },
    watch: {
        folder() {
            this.page = 0
            this.loadBlocks()
        },
        search() {
            this.page = 0
            this.debouncedLoadBlocks()
        },
    },
}
</script>

<style scoped>
.pfadd-sheet {
    background: var(--v-surface-base);
    border-top: 1px solid var(--v-outline-base);
    border-radius: 20px 20px 0 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 88vh;
    width: 100%;
}

.pfadd-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 16px 12px;
    border-bottom: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.pfadd-back {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
    flex-shrink: 0;
}

.pfadd-back .v-icon {
    color: inherit !important;
}

.pfadd-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.pfadd-body {
    flex: 1;
    overflow-y: auto;
    padding: 14px 16px calc(14px + var(--safe-area-inset-bottom, 0px));
}

@media (max-width: 959px) {
    .pfadd-body::-webkit-scrollbar {
        display: none;
    }
}

.ab-filters {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 12px;
}

.ab-msg {
    text-align: center;
    padding: 28px 0;
    color: var(--v-txtMuted-base);
}

.ab-list {
    display: flex;
    flex-direction: column;
}

.ab-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 4px;
    border-bottom: 1px solid var(--v-outline-base);
    cursor: pointer;
    transition: background 0.12s;
}

.ab-row:hover {
    background: var(--v-surface2-base);
}

.ab-icon {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.ab-info {
    flex: 1;
    min-width: 0;
}

.ab-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.ab-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.ab-add {
    flex-shrink: 0;
    background: var(--v-surface2-base) !important;
}

.theme--dark .ab-add {
    color: var(--v-primary-base) !important;
}

.theme--light .ab-add {
    color: var(--v-accent-base) !important;
}
</style>
